import { REMOVE_MIN_COUNT, MAX_LEN } from "./const";
import { node, icon, createPremiumIcon } from "~/util/dom";
import { getReportReasonsList } from "~/util/review";
import { reportReview } from "~/util/providers/review";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import Notifications, { NOTIFICATIONS_TYPE } from "~/util/notifications";
import { store } from "~/state/store";

interface IFirmReviewReport {
	id: number;
	isFirm?: boolean;
	isTwin?: boolean;
	isOwner?: boolean;
	hasPhotos?: boolean;
	logSource?: string;
	analytics?: object;
}

function updateDetail(reviewId: number) {
	const logObj = {
		detail: {
			reviewId,
		},
	};

	window.dispatchEvent(new CustomEvent("review-report", logObj));
}

// hlaseni chyby ke komentari
export default class FirmReviewReport {
	_opts: IFirmReviewReport;

	_dom: {
		[key: string]: null | HTMLElement;
		text?: HTMLTextAreaElement;
	};

	_isOtherChecked: boolean;

	_reasonsList: Array<{
		id: number;
		transText: string;
		key?: string;
		fillText?: boolean;
	}>;

	constructor(optsArg: IFirmReviewReport) {
		const opts = {
			id: 0,
			isFirm: false,
			isTwin: false,
			isOwner: false,
			hasPhotos: false,
			logSource: "",
			analytics: {},
			...optsArg,
		};

		this._opts = opts;
		this._dom = {
			container: node("div", { className: "firm-review-report" }),
			cover: node("div", { className: "firm-review-report-cover" }),
		};
		this._isOtherChecked = false;
		this._reasonsList = getReportReasonsList(this._opts.isFirm, this._opts.hasPhotos);

		this._build();
		this._update();
	}

	destroy() {
		document.body.removeEventListener("keydown", this);
		this._dom.cover.remove();
	}

	handleEvent(event: KeyboardEvent) {
		switch (event.type) {
			case "keydown":
				if (event.keyCode === 27) {
					this.close();
				}
				break;
			default:
		}
	}

	close() {
		this.destroy();
	}

	_build() {
		const title = node("h2", { textContent: _("detail.content.review2.reportHeader") });
		const reasons = node("div", { className: "reasons" });
		const closeSvg = icon("close");

		closeSvg.addEventListener("click", () => {
			this.close();
		});

		this._dom.container.appendChild(closeSvg);
		this._dom.container.appendChild(title);

		// info o premiu
		const { premium } = store.getState().user.user;

		if (premium) {
			const premiumInfo = node("div", { className: "reportreview-premium-info" });

			premiumInfo.append(createPremiumIcon(), node("p", { innerHTML: _("report.premiumInfo") }));
			this._dom.container.appendChild(premiumInfo);
		}

		this._dom.container.appendChild(reasons);

		this._reasonsList.forEach((item, ind) => {
			const label = node("label", { className: "radiocheck" });
			const input = node("input", { type: "radio", name: "reportReason", value: item.id.toString() });

			if (ind === 0) {
				input.checked = true;
			}

			const span = node("span", { textContent: _(item.transText) });

			label.appendChild(input);
			label.appendChild(span);
			reasons.appendChild(label);

			input.addEventListener("change", () => {
				this._isOtherChecked = item.fillText && input.checked;

				this._update();
			});
		});

		const otherTitle = node("h3", { textContent: _("badPhoto.note") });
		const text = node("textarea", { placeholder: _("detail.content.review2.reportPlaceholder") });

		text.addEventListener("input", () => {
			this._update();
		});

		const counter = node("p", { className: "counter" });
		const error = node("p", { className: "error" });
		const buttons = node("div", { className: "buttons" });
		const sendBtn = node("button", { className: "send-btn", textContent: _("submit") });

		sendBtn.setAttribute("data-log-ignore", "");
		sendBtn.addEventListener("click", () => {
			// analytika pro mapy
			Mapy.getComponent("logger").log("click", {
				text: "Odeslat problem",
				source: this._opts.logSource,
				section: "reviews",
				...this._opts.analytics,
			});
			this._send();
		});

		const cancelBtn = node("button", { className: "cancel-btn", textContent: _("cancel") });

		cancelBtn.setAttribute("data-log-ignore", "");
		cancelBtn.addEventListener("click", () => {
			// analytika pro mapy
			Mapy.getComponent("logger").log("click", {
				text: "Zrusit nahlasení problemu",
				source: this._opts.logSource,
				section: "reviews",
				...this._opts.analytics,
			});
			this.close();
		});
		buttons.appendChild(sendBtn);
		buttons.appendChild(cancelBtn);
		this._dom.container.appendChild(otherTitle);
		this._dom.container.appendChild(text);
		this._dom.container.appendChild(counter);
		this._dom.container.appendChild(error);
		this._dom.container.appendChild(buttons);

		Object.assign(this._dom, { text, error, counter, sendBtn });

		this._dom.cover.append(this._dom.container);
		document.body.appendChild(this._dom.cover);
		document.body.addEventListener("keydown", this);
	}

	_send() {
		this._getData().then(data => {
			const text = this._dom.text.value.trim();

			this._dom.error.textContent = "";
			this._dom.text.classList.remove("error");

			if (data.fillText && !text) {
				this._dom.error.textContent = _("badPhoto.errors.note");
				this._dom.text.classList.add("error");
				this._dom.text.focus();

				return;
			}

			reportReview({
				reviewId: this._opts.id,
				reason: data.reason,
				text,
			}).then(() => {
				// analytika pro mapy
				Mapy.getComponent("logger").log("problem-leti", {
					type: data.type,
					text: text.length ? 1 : 0,
					source: this._opts.logSource,
					section: "reviews",
					photos: this._opts.hasPhotos ? 1 : 0,
					...this._opts.analytics,
				});

				updateDetail(this._opts.id);
				this._createCloseDialog(_("badPhoto.thanksHeader"), _("detail.content.review2.report.success"));
			}, event => {
				// eslint-disable-next-line no-console
				console.error(event);

				if (event.status === 485) {
					this._createCloseDialog(_("detail.content.review2.report.error485Header"), _("detail.content.review2.report.error485Text"));
				} else {
					let errorMsg = "";

					switch (event.status) {
						case 429:
							errorMsg = _(this._opts.isOwner ? "detail.content.review2.report.error429Owner" : "detail.content.review2.report.error429Review");
							break;
						default:
							errorMsg = _("commonError");
					}

					this.close();
					Notifications.notify({
						content: errorMsg,
						type: NOTIFICATIONS_TYPE.ERROR,
					});
				}
			});
		}, () => { /**/ });
	}

	_getData(): Promise<{
		reason: string;
		fillText: boolean;
		type: number;
	}> {
		return new Promise(resolve => {
			const inputEl: HTMLInputElement = this._dom.container.querySelector("input:checked");
			const type = parseFloat(inputEl.value);
			const selReason = this._reasonsList.filter(item => item.id === type);

			if (this._opts.isFirm) {
				const reason = selReason.length ? _(selReason[0].transText) : _(this._reasonsList[0].transText);
				const output = {
					reason,
					fillText: selReason.length ? selReason[0].fillText || false : false,
					type,
				};

				// nejsme cz ani sk
				if (["cs", "sk"].indexOf(CONFIG.defaultLang) === -1) {
					// ulozime aktualni jazyk
					const curLang = Userweb.defaultLang;

					Mapy.Util.loadScript(`/js/lang-cs.${CONFIG.version}.js`).then(() => {
						// nastavime
						Userweb.defaultLang = curLang;

						try {
							const langKey = `lang.${selReason[0].transText}`;

							output.reason = i18nData[langKey]();
							resolve(output);
						} catch (error) {
							// eslint-disable-next-line no-console
							console.error(error);
							resolve(output);
						}
					}, error => {
						// eslint-disable-next-line no-console
						console.error(error);
						resolve(output);
					});
				} else {
					resolve(output);
				}
			} else {
				const reason = selReason.length ? selReason[0].key : this._reasonsList[0].key;

				resolve({
					reason,
					fillText: selReason.length ? selReason[0].fillText || false : false,
					type,
				});
			}
		});
	}

	_createCloseDialog(header: string, message: string) {
		// udelame novy
		this._dom.container.innerHTML = "";
		this._dom.container.classList.add("sended");

		const title = node("h2", { textContent: header });
		const desc = node("p", { className: "desc", textContent: message });
		const buttons = node("div", { className: "buttons" });
		const closeBtn = node("button", { className: "cancel-btn", textContent: _("close") });

		closeBtn.setAttribute("data-log-ignore", "");
		closeBtn.addEventListener("click", () => {
			this.close();
		});

		buttons.appendChild(closeBtn);
		this._dom.container.appendChild(title);
		this._dom.container.appendChild(desc);
		this._dom.container.appendChild(buttons);
	}

	_update() {
		const count = this._dom.text.value.length;

		this._dom.sendBtn.removeAttribute("disabled");

		// omezeni textarey
		if ((count >= 1 && count <= REMOVE_MIN_COUNT - 1) || (this._isOtherChecked && count < REMOVE_MIN_COUNT)) {
			this._dom.sendBtn.setAttribute("disabled", "true");
		}

		const chars = MAX_LEN - count;

		this._dom.counter.innerHTML = `${_("detail.content.review.numOfChar", {
			chars,
		})} `;

		const minCount = node("span", { className: "min-count" });

		minCount.textContent = `(${_("detail.content.review2.remove.minCount")})`;
		this._dom.counter.appendChild(minCount);
	}
}
